import type { Table } from '@tanstack/react-table';
import { CheckIcon, PlusCircleIcon, Settings2Icon, XIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../../lib/utils.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../ui/command.js';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu.js';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover.js';
import { Separator } from '../ui/separator.js';

export interface FacetOption {
  value: string;
  label: string;
  icon?: ReactNode;
}

export interface DataTableFacet {
  columnId: string;
  title: string;
  options: FacetOption[];
}

export interface DataTableToolbarProps<TData> {
  table: Table<TData>;
  /** Rendered on the left — typically a search field bound to the query state. */
  search?: ReactNode;
  facets?: DataTableFacet[];
  /** Whether any filter is active, for the reset button. Server-driven tables must pass this. */
  isFiltered?: boolean;
  onReset?: () => void;
  actions?: ReactNode;
  className?: string;
}

export function DataTableToolbar<TData>({
  table,
  search,
  facets = [],
  isFiltered,
  onReset,
  actions,
  className,
}: DataTableToolbarProps<TData>) {
  const filtered = isFiltered ?? table.getState().columnFilters.length > 0;

  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-2', className)}>
      <div className="flex flex-1 flex-wrap items-center gap-2">
        {search}
        {facets.map((facet) => (
          <DataTableFacetFilter key={facet.columnId} table={table} {...facet} />
        ))}
        {filtered && onReset && (
          <Button variant="ghost" size="sm" onClick={onReset}>
            Reset
            <XIcon aria-hidden="true" />
          </Button>
        )}
      </div>

      <div className="flex items-center gap-2">
        {actions}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Settings2Icon aria-hidden="true" />
              View
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {table
              .getAllColumns()
              .filter((column) => column.getCanHide())
              .map((column) => (
                <DropdownMenuCheckboxItem
                  key={column.id}
                  className="capitalize"
                  checked={column.getIsVisible()}
                  onCheckedChange={(value) => column.toggleVisibility(!!value)}
                >
                  {column.id}
                </DropdownMenuCheckboxItem>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

function DataTableFacetFilter<TData>({
  table,
  columnId,
  title,
  options,
}: { table: Table<TData> } & DataTableFacet) {
  const column = table.getColumn(columnId);
  if (!column) return null;

  const selected = new Set((column.getFilterValue() as string[] | undefined) ?? []);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="border-dashed">
          <PlusCircleIcon aria-hidden="true" />
          {title}
          {selected.size > 0 && (
            <>
              <Separator orientation="vertical" className="mx-0.5 h-4" />
              <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                {selected.size}
              </Badge>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-0" align="start">
        <Command>
          <CommandInput placeholder={title} />
          <CommandList>
            <CommandEmpty>No results.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selected.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      const next = new Set(selected);
                      if (isSelected) next.delete(option.value);
                      else next.add(option.value);
                      // undefined rather than [] — an empty array still counts as an
                      // active filter and would keep the reset button lit.
                      column.setFilterValue(next.size > 0 ? Array.from(next) : undefined);
                    }}
                  >
                    <span
                      className={cn(
                        'flex size-4 items-center justify-center rounded-[4px] border',
                        isSelected
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-input',
                      )}
                      aria-hidden="true"
                    >
                      {isSelected && <CheckIcon className="size-3" />}
                    </span>
                    {option.icon}
                    {option.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {selected.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => column.setFilterValue(undefined)}
                    className="justify-center"
                  >
                    Clear
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
